import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { useAuth } from './auth';
import { LoginPage } from './auth/LoginPage';
import { OAuthCallback } from './auth/OAuthCallback';
import { EmbarkFlow } from './embark/EmbarkFlow';
import { DebugConsole } from './log/DebugConsole';
import { useDebugStore } from './log/debug-store';
import { Shell } from './shell';
import { ToastStack } from './toasts/ToastStack';

function DebugKeybind(): null {
  const toggle = useDebugStore((s) => s.toggle);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.code !== 'Backquote') return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      e.preventDefault();
      toggle();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toggle]);
  return null;
}

function RequireAuth({ children }: { children: JSX.Element }): JSX.Element {
  const status = useAuth((s) => s.status);

  if (status === 'unknown' || status === 'loading') {
    return (
      <div style={{
        height: '100vh',
        background: '#1e1f22',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#949ba4',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}>
        Loading...
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace />;
  }

  return children;
}

export function App(): JSX.Element {
  const debugOpen = useDebugStore((s) => s.open);
  const initialize = useAuth((s) => s.initialize);
  const status = useAuth((s) => s.status);

  useEffect(() => {
    if (window.location.pathname === '/oauth-callback') return;
    if (status === 'unknown') {
      void initialize();
    }
  }, [initialize, status]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/oauth-callback" element={<OAuthCallback />} />
        <Route
          path="/embark"
          element={
            <RequireAuth>
              <EmbarkFlow />
            </RequireAuth>
          }
        />
        <Route
          path="/play/:claimId"
          element={
            <RequireAuth>
              <Shell />
            </RequireAuth>
          }
        />
        <Route path="*" element={<RootRedirect />} />
      </Routes>
      <ToastStack />
      <DebugKeybind />
      {debugOpen && <DebugConsole />}
    </BrowserRouter>
  );
}

function RootRedirect(): JSX.Element {
  const status = useAuth((s) => s.status);

  if (status === 'authenticated') {
    return <Navigate to="/embark" replace />;
  }
  if (status === 'unknown' || status === 'loading') {
    return (
      <div style={{
        height: '100vh',
        background: '#1e1f22',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#949ba4',
      }}>
        Loading...
      </div>
    );
  }
  return <Navigate to="/login" replace />;
}
